import { useMemo } from 'react'

import { useQuery } from '@api/hooks'
import { AutocompleteProps, SxProps, Theme } from '@mui/material'
import MarketBasketsApi, { MarketBasketsKeys } from '@services/marketBaskets.api'
import { FieldValues, Path } from 'react-hook-form'

import RHFAutocomplete from './RHFAutocomplete'

type CustomerOption = { name: string; plid: string }

type Props<T extends FieldValues> = Omit<
	AutocompleteProps<CustomerOption, false, false, false>,
	'renderInput' | 'options'
> & {
	name: Path<T>
	label: string
	marketBasketId: Id
	required?: boolean
	fullWidth?: boolean
	sx?: SxProps<Theme>
}

export default function BasketCustomerAutocomplete<T extends FieldValues>({
	marketBasketId,
	required = false,
	...props
}: Props<T>) {
	const { data: customers, isLoading } = useQuery({
		queryKey: MarketBasketsKeys.customers(marketBasketId),
		queryFn: () => MarketBasketsApi.customers(marketBasketId),
		enabled: !!marketBasketId,
	})

	const options = useMemo<CustomerOption[]>(
		() => customers?.map((c) => ({ name: c.label, plid: String(c.id) })) ?? [],
		[customers]
	)

	return (
		<RHFAutocomplete
			{...props}
			required={required}
			options={options}
			loading={isLoading}
			getOptionLabel={(option: CustomerOption) => option.name ?? ''}
			isOptionEqualToValue={(option: CustomerOption, value: CustomerOption) =>
				option.plid === value.plid
			}
		/>
	)
}
